import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { z } from "zod";
import { MobileFrame } from "@/components/MobileFrame";
import { Chip, Hairline, RubberStamp, Swatch, type SwatchKind } from "@/components/primitives";
import { useAuth } from "@/hooks/useAuth";
import { useFollow } from "@/context/FollowContext";
import { supabase } from "@/integrations/supabase/client";
import { INTERESTS, MAX_INTERESTS, MIN_INTERESTS } from "@/lib/interests";
import { tourBus, tourStore } from "@/lib/tour";
import { useTheme } from "@/context/ThemeContext";
import { THEMES, THEME_IDS, type ThemeId } from "@/lib/themes";

// ─────────────────────────────────────────────────────────────────────────────
// Tutorial — first-run walkthrough shown right after signup. Eight quick beats:
//   1. Welcome / what Fig is
//   2. Scan walkthrough (point camera at a garment)
//   3. Verify (PASS / FAIL stamp explanation)
//   4. Price & list (live comps)
//   5. Location permission — asked upfront so Nearby works from minute one
//   6. Community — how to follow collectors, browse the feed, and set grails
//   7. Interests — pick 3-5 tags so /home can personalise from day one
//   8. Profile setup — handle, display name, avatar swatch
// We persist completion in localStorage keyed to the user id so it never re-shows
// for the same account, even after sign-out / sign-in.
// ─────────────────────────────────────────────────────────────────────────────

const DONE_KEY = (uid: string) => `fig.tutorial.done.${uid}`;

const AVATAR_OPTIONS: SwatchKind[] = ["cream", "rust", "olive", "ink", "stripes"];

// Handles: lowercase, alphanumeric + underscore, 3-20 chars. Display name: any
// non-empty trimmed string up to 40 chars. We validate before hitting Supabase.
const handleSchema = z
  .string()
  .trim()
  .min(3, "Handle must be at least 3 characters")
  .max(20, "Handle must be 20 characters or fewer")
  .regex(/^[a-z0-9_]+$/, "Lowercase letters, numbers, and _ only");

const displayNameSchema = z
  .string()
  .trim()
  .min(1, "Display name can't be empty")
  .max(40, "Display name must be 40 characters or fewer");

interface Step {
  kicker: string;
  title: string;
  body: string;
  illustration: React.ReactNode;
}

export default function Tutorial() {
  const nav = useNavigate();
  const location = useLocation();
  // Replay mode: launched from Profile › Field kit › Restart. We just walk
  // through the steps for reference. Replay now hands off to the interactive
  // TourOverlay (real app + coachmarks) so the explainer matches what users
  // actually do — no static slide deck, no profile rewrite.
  const isReplay = (location.state as { replay?: boolean } | null)?.replay === true;
  const { user, loading } = useAuth();
  const { theme, setTheme } = useTheme();

  // Replay path: clear the per-user "done" flag so the overlay treats this as
  // fresh, kick off the tour, and bail to /home where step 1 lands.
  useEffect(() => {
    if (!isReplay || !user) return;
    tourStore.reset(user.id);
    nav("/home", { replace: true });
    // Defer one tick so the route change finishes before the overlay shows.
    // We intentionally do NOT clean this up — the cleanup runs immediately on
    // unmount (caused by the nav above), which would cancel the start() call
    // and leave the user stranded on /home with no tour.
    window.setTimeout(() => tourBus.start(), 80);
  }, [isReplay, user, nav]);

  const [stepIdx, setStepIdx] = useState(0);
  // Home base — typed city for the Nearby feed. The skip path stores empty
  // and the user can fill it later from Settings → Privacy → Home base.
  const [homeBase, setHomeBase] = useState("");
  const [homeBaseSkipped, setHomeBaseSkipped] = useState(false);
  // Training-data consent — explicit opt-in. Defaults to false (unchecked)
  // per Apple App Store guidelines + GDPR Article 7. User must affirmatively
  // tick the box. Persisted in finish() to both profiles.allow_training_use
  // and a row in the consents audit-trail table.
  const [allowTraining, setAllowTraining] = useState(false);
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);

  // Profile-step state — prefilled from the existing row created by the
  // handle_new_user trigger so users see their auto-generated handle and can
  // tweak it instead of starting from scratch.
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [avatar, setAvatar] = useState<SwatchKind>("cream");
  const [interests, setInterests] = useState<string[]>([]);
  const [profileErrors, setProfileErrors] = useState<{ handle?: string; displayName?: string }>({});
  const [savingProfile, setSavingProfile] = useState(false);

  const toggleInterest = (value: string) => {
    setInterests((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_INTERESTS) return prev; // cap silently — UI shows the limit
      return [...prev, value];
    });
  };

  // If somehow they hit /tutorial without an account, push them to auth.
  useEffect(() => {
    if (!loading && !user) nav("/auth", { replace: true });
  }, [loading, user, nav]);

  // Home base is set via the typed input in step 04 and persisted from
  // `finish()` below alongside the rest of the profile. No GPS path.

  // Prefill the profile-step inputs from the row Supabase created on signup.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("handle, display_name, avatar, tags")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      setHandle(data.handle ?? "");
      setDisplayName(data.display_name ?? data.handle ?? "");
      const a = data.avatar as SwatchKind;
      if (AVATAR_OPTIONS.includes(a)) setAvatar(a);
      // Only prefill interests we know about, capped at MAX_INTERESTS.
      const known = new Set(INTERESTS.map((i) => i.value));
      const existing = (data.tags ?? []).filter((t: string) => known.has(t)).slice(0, MAX_INTERESTS);
      if (existing.length) setInterests(existing);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const steps: Step[] = useMemo(() => [
    {
      kicker: "01 · welcome",
      title: "You're in.",
      body: "Fig is your pocket field guide for vintage. Scan anything you find — thrift store, estate sale, your grandma's closet — and we'll tell you what it is, when it's from, and what it's worth.",
      illustration: (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <RubberStamp label="WELCOME" sub="COLLECTOR" color="hsl(var(--rust))" />
        </div>
      ),
    },
    {
      kicker: "02 · profile",
      title: "Stamp your card.",
      body: "Pick a handle other collectors will know you by, your display name, an avatar swatch, and the paper your field kit is printed on. You can change all of this later.",
      illustration: (
        <ProfileStep
          handle={handle}
          displayName={displayName}
          avatar={avatar}
          errors={profileErrors}
          theme={theme}
          onTheme={(id) => { setTheme(id); }}
          onHandle={(v) => { setHandle(v.toLowerCase()); setProfileErrors((e) => ({ ...e, handle: undefined })); }}
          onDisplayName={(v) => { setDisplayName(v); setProfileErrors((e) => ({ ...e, displayName: undefined })); }}
          onAvatar={setAvatar}
        />
      ),
    },
    {
      kicker: "03 · interests",
      title: "What are you hunting?",
      body: `Pick ${MIN_INTERESTS}–${MAX_INTERESTS} tags. Fig uses these to personalise your Home discovery surface — featured finds, trending brands, and grail leads will lean toward what you're into.`,
      illustration: (
        <InterestsStep selected={interests} onToggle={toggleInterest} />
      ),
    },
    {
      kicker: "04 · follow",
      title: "Some collectors you'd vibe with.",
      body: "These collectors share the most overlap with what you're hunting. Tap Follow to add anyone to your feed — you can always tweak this later from their profile.",
      illustration: (
        <FollowStep interests={interests} />
      ),
    },
    {
      kicker: "05 · home base",
      title: "Where are you collecting from?",
      body: "Type your city (e.g. \"Brooklyn, NY\"). Fig matches you with other collectors who typed the same city — no GPS, no tracking. You can skip this and set it later from Settings.",
      illustration: (
        <HomeBaseStep
          value={homeBase}
          onChange={(v) => { setHomeBase(v); setHomeBaseSkipped(false); }}
          onSkip={() => { setHomeBase(""); setHomeBaseSkipped(true); }}
          skipped={homeBaseSkipped}
        />
      ),
    },
    {
      kicker: "06 · help improve fig",
      title: "Help Fig get smarter.",
      body: "Your scans, photos, and verdicts are what teach the engine. With your permission, we'll use them to train future versions of Fig Verify. You can change this anytime from Settings → Privacy.",
      illustration: (
        <ConsentStep
          allowTraining={allowTraining}
          onToggle={setAllowTraining}
          acknowledged={consentAcknowledged}
          onAcknowledge={setConsentAcknowledged}
          onNavigate={(path) => nav(path)}
        />
      ),
    },
  ], [homeBase, homeBaseSkipped, allowTraining, consentAcknowledged, handle, displayName, avatar, profileErrors, interests, theme, setTheme, nav]);

  // Replay mode is a "how the app works" refresher — strip out the signup-only
  // beats (profile setup + interests + location) so returning users only see the
  // explainer steps. We key on kicker prefix so the filter survives reorders.
  const activeSteps = useMemo(
    () => isReplay
      ? steps.filter((s) => !["02", "03", "04", "05", "06"].some((p) => s.kicker.startsWith(p)))
      : steps,
    [steps, isReplay],
  );

  const step = activeSteps[stepIdx];
  const isLast = stepIdx === activeSteps.length - 1;
  const currentKicker = step?.kicker ?? "";
  const isProfileStep = currentKicker.startsWith("02");
  const isInterestsStep = currentKicker.startsWith("03");
  // Step numbering after FollowStep was inserted between Interests (03)
  // and HomeBase: 04 = follow, 05 = home base, 06 = consent.
  const isFollowStep = currentKicker.startsWith("04");
  const isHomeBaseStep = currentKicker.startsWith("05");
  const isConsentStep = currentKicker.startsWith("06");
  // On the home-base step: typed city OR explicit skip.
  // On the consent step: the user must tick the "I've read…" acknowledgement
  // (the training-use checkbox itself is optional — false = no, true = yes).
  // On the interests step, they need at least MIN_INTERESTS picked. On the
  // profile step, basic shape validation must pass before they can finish.
  const profileShapeOk =
    handleSchema.safeParse(handle).success && displayNameSchema.safeParse(displayName).success;
  // Follow step is always skippable — no gate.
  const canAdvance = isFollowStep
    ? true
    : isHomeBaseStep
    ? (homeBase.trim().length > 0 || homeBaseSkipped)
    : isConsentStep
    ? consentAcknowledged
    : isInterestsStep
      ? (isReplay || interests.length >= MIN_INTERESTS)
      : isProfileStep
        ? (isReplay || (profileShapeOk && !savingProfile))
        : true;

  const finish = async () => {
    if (!user) return;
    // Replay mode: skip the profile rewrite entirely — they're just refreshing
    // their memory. Drop them back where they came from.
    if (isReplay) {
      nav("/you", { replace: true });
      return;
    }
    // Validate one more time before write — defensive, since canAdvance can be
    // bypassed by keyboard if state changes mid-tap.
    const h = handleSchema.safeParse(handle);
    const d = displayNameSchema.safeParse(displayName);
    if (!h.success || !d.success) {
      setProfileErrors({
        handle: h.success ? undefined : h.error.issues[0]?.message,
        displayName: d.success ? undefined : d.error.issues[0]?.message,
      });
      return;
    }

    setSavingProfile(true);
    try {
      // Server-side handle uniqueness check. The DB also has a unique
      // constraint, but we want a friendlier error than a Postgres code.
      const { data: taken } = await supabase
        .from("profiles")
        .select("id")
        .eq("handle", h.data)
        .neq("id", user.id)
        .maybeSingle();
      if (taken) {
        setProfileErrors({ handle: "That handle is already taken" });
        setSavingProfile(false);
        return;
      }

      const trimmedHome = homeBase.trim().slice(0, 80);
      const homeUpdate = trimmedHome
        ? { location: trimmedHome, city: trimmedHome }
        : {};
      const { error } = await supabase
        .from("profiles")
        .update({
          handle: h.data,
          display_name: d.data,
          avatar,
          tags: interests,
          allow_training_use: allowTraining,
          // Flip onboarding_complete to true so the first-time
          // /home WalkthroughOverlay knows the user is past
          // setup. walkthrough_complete stays false (its default)
          // so the spotlight tour gets a chance to run; it'll
          // flip to true when the user finishes or dismisses
          // the tour.
          onboarding_complete: true,
          walkthrough_complete: false,
          ...homeUpdate,
        })
        .eq("id", user.id);
      if (error) throw error;

      // Audit trail — record the consent decision (granted or not) at
      // onboarding. The `consents` table is append-only so revocations later
      // from Settings show up as separate rows. Non-fatal: if this write
      // fails the user still completes onboarding, but we log a warning.
      await supabase
        .from("consents")
        .insert({
          user_id: user.id,
          kind: "allow_training_use",
          granted: allowTraining,
          context: { source: "onboarding" },
        })
        .then(({ error: cErr }) => {
          if (cErr) console.warn("consent log failed:", cErr.message);
        });

      try { localStorage.setItem(DONE_KEY(user.id), "1"); } catch { /* quota */ }
      toast.success("Field kit ready.");
      // Hand off to the interactive walkthrough so first-run users learn the
      // app inside the app instead of reading more slides.
      tourStore.reset(user.id);
      nav("/home", { replace: true });
      window.setTimeout(() => tourBus.start(), 80);
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't save your profile — try again.");
      setSavingProfile(false);
    }
  };

  const next = () => {
    if (!canAdvance) return;
    if (isLast) finish();
    else setStepIdx((i) => i + 1);
  };

  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  // Replay just hands off to the interactive overlay; render nothing so the
  // static deck doesn't flash before the redirect lands.
  if (isReplay) return null;

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "44px 24px 32px", display: "flex", flexDirection: "column" }}>
        {/* Masthead */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span className="caps">FIG · FIELD KIT</span>
          {isReplay && (
            <button
              onClick={() => nav("/you")}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              aria-label="Exit tutorial"
            >
              <span className="caps" style={{ color: "hsl(var(--rust))" }}>Exit ✕</span>
            </button>
          )}
        </div>

        {/* Step dots */}
        <div style={{ display: "flex", gap: 6, marginTop: 18, marginBottom: 22 }}>
          {activeSteps.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 3,
                background: i <= stepIdx ? "hsl(var(--ink))" : "hsl(var(--paper-3))",
                transition: "background 200ms",
              }}
            />
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Chip>{step.kicker}</Chip>
          <h1 style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 900,
            fontSize: 44,
            lineHeight: 1.02,
            color: "hsl(var(--ink))",
            letterSpacing: "-0.03em",
            margin: "14px 0 14px",
          }}>
            {step.title}
          </h1>
          <p style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 17,
            lineHeight: 1.45,
            color: "hsl(var(--ink-2))",
            margin: 0,
          }}>
            {step.body}
          </p>

          <div style={{ margin: "24px 0" }}>
            <Hairline />
          </div>

          <div style={{ marginTop: 4 }}>{step.illustration}</div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          {stepIdx > 0 && (
            <button
              onClick={back}
              style={{
                padding: "16px 20px",
                background: "transparent",
                color: "hsl(var(--ink))",
                border: "1px solid hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              ←
            </button>
          )}
          <button
            onClick={next}
            disabled={!canAdvance}
            className="btn btn-primary"
            style={{
              flex: 1,
              padding: "16px",
              opacity: canAdvance ? 1 : 0.5,
              cursor: canAdvance ? "pointer" : "not-allowed",
            }}
          >
            {savingProfile ? "SAVING…" : isLast ? (isReplay ? "Done →" : "Enter Fig →") : "Next →"}
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}

// ─── Location step illustration ─────────────────────────────────────────────

interface HomeBaseStepProps {
  value: string;
  onChange: (v: string) => void;
  onSkip: () => void;
  skipped: boolean;
}

function HomeBaseStep({ value, onChange, onSkip, skipped }: HomeBaseStepProps) {
  if (skipped) {
    return (
      <div style={{
        padding: 16,
        background: "hsl(var(--paper-2))",
        border: "1px dashed hsl(var(--ink))",
        textAlign: "center",
      }}>
        <span className="caps" style={{ color: "hsl(var(--rust))" }}>SKIPPED FOR NOW</span>
        <div style={{ marginTop: 8, fontSize: 13, color: "hsl(var(--ink-2))", fontFamily: "'Fraunces', 'Playfair Display', serif", lineHeight: 1.4 }}>
          You can set your home base later from Settings → Privacy. The Nearby feed will stay empty until you do.
        </div>
      </div>
    );
  }

  const hasValue = value.trim().length > 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div>
        <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 6, fontSize: 10, letterSpacing: "0.1em" }}>
          Your city
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Brooklyn, NY"
          maxLength={80}
          style={{
            width: "100%",
            padding: "12px 14px",
            border: "1px solid hsl(var(--ink))",
            background: "hsl(var(--paper))",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: "hsl(var(--ink))",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {hasValue && (
          <div style={{ marginTop: 6, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: "hsl(var(--olive))", letterSpacing: "0.08em" }}>
            ✓ Will match other collectors in {value.trim()}
          </div>
        )}
      </div>
      <button
        onClick={onSkip}
        style={{
          padding: "12px",
          background: "transparent",
          color: "hsl(var(--ink-2))",
          border: "1px dashed hsl(var(--ink-2))",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Skip for now
      </button>
    </div>
  );
}

// ─── Consent step illustration ──────────────────────────────────────────────

interface ConsentStepProps {
  allowTraining: boolean;
  onToggle: (v: boolean) => void;
  acknowledged: boolean;
  onAcknowledge: (v: boolean) => void;
  onNavigate: (path: string) => void;
}

function ConsentStep({ allowTraining, onToggle, acknowledged, onAcknowledge, onNavigate }: ConsentStepProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{
        padding: 14,
        border: "1px solid hsl(var(--ink))",
        background: "hsl(var(--paper-2, var(--cream)))",
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <input
          type="checkbox"
          id="tutorial-allow-training"
          checked={allowTraining}
          onChange={(e) => onToggle(e.target.checked)}
          style={{ marginTop: 3, accentColor: "hsl(var(--rust))", flexShrink: 0 }}
        />
        <label htmlFor="tutorial-allow-training" style={{ flex: 1, cursor: "pointer" }}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
            color: "hsl(var(--ink))",
          }}>
            Help improve Fig
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "hsl(var(--ink-2))", lineHeight: 1.4, marginTop: 4,
          }}>
            Let Fig use your scans, photos, and verdicts to train future versions of the engine. Optional — you can turn this on or off anytime from Settings → Privacy.
          </div>
        </label>
      </div>

      <div style={{
        padding: 14,
        border: "1px solid hsl(var(--ink))",
        background: "hsl(var(--paper-2, var(--cream)))",
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <input
          type="checkbox"
          id="tutorial-acknowledge-tos"
          checked={acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
          style={{ marginTop: 3, accentColor: "hsl(var(--rust))", flexShrink: 0 }}
        />
        <label htmlFor="tutorial-acknowledge-tos" style={{ flex: 1, cursor: "pointer" }}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
            color: "hsl(var(--ink))", lineHeight: 1.5,
          }}>
            I've read and agree to Fig's{" "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate("/terms");}}
              style={{
                background: "transparent", border: "none", padding: 0,
                color: "hsl(var(--rust))", textDecoration: "underline",
                fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
              }}
            >
              Terms of Service
            </button>
            {" "}and{" "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate("/privacy");}}
              style={{
                background: "transparent", border: "none", padding: 0,
                color: "hsl(var(--rust))", textDecoration: "underline",
                fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
              }}
            >
              Privacy Policy
            </button>
            .
          </div>
        </label>
      </div>
    </div>
  );
}

// ─── Follow step illustration ───────────────────────────────────────────────
// Suggested collectors sorted by interest-tag overlap (intersection size).
// Show up to 20 rows. If fewer than 20 high-overlap matches exist, fill
// the tail with featured collectors (top by recent posting activity).
// Each row has a Follow / Following toggle button; the user can pick zero
// or many — the step is fully skippable.

interface FollowSuggestion {
  id: string;
  handle: string;
  display_name: string | null;
  avatar: string;
  tags: string[];
  overlap: number;
}

function FollowStep({ interests }: { interests: string[] }) {
  const { user } = useAuth();
  const { isFollowing, toggle } = useFollow();
  const [rows, setRows] = useState<FollowSuggestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // Pull a generous candidate pool of public profiles. We score
      // client-side rather than push the array-overlap to SQL — the
      // pool is small (early platform) and the math is trivial.
      const { data } = await (supabase as any)
        .from("profiles_public")
        .select("id, handle, display_name, avatar, tags")
        .neq("id", user.id)
        .eq("is_private", false)
        .limit(200);
      if (cancelled) return;
      const all: FollowSuggestion[] = ((data ?? []) as Array<{
        id: string; handle: string; display_name: string | null;
        avatar: string; tags: string[] | null;
      }>)
        .filter((p) => !!p.handle)
        .map((p) => ({
          id: p.id,
          handle: p.handle,
          display_name: p.display_name,
          avatar: p.avatar ?? "olive",
          tags: Array.isArray(p.tags) ? p.tags : [],
          overlap: 0,
        }));

      // Score: how many of MY interests this profile also tagged.
      const mySet = new Set(interests);
      for (const r of all) {
        let o = 0;
        for (const t of r.tags) if (mySet.has(t)) o++;
        r.overlap = o;
      }
      // Matched bucket: at least one shared interest, sorted by overlap.
      const matched = all
        .filter((r) => r.overlap > 0)
        .sort((a, b) => b.overlap - a.overlap);
      // Featured filler when the matched pool < 20 (early platform).
      // "Featured" here = recently active public profiles not already in
      // matched. Pulled by recency on profiles_public.
      let featured: FollowSuggestion[] = [];
      if (matched.length < 20) {
        const matchedIds = new Set(matched.map((r) => r.id));
        featured = all
          .filter((r) => !matchedIds.has(r.id))
          .slice(0, 20 - matched.length);
      }
      const combined = [...matched, ...featured].slice(0, 20);
      if (!cancelled) {
        setRows(combined);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, interests]);

  if (loading) {
    return (
      <div style={{
        padding: "32px 12px", textAlign: "center",
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
        letterSpacing: "0.15em", color: "hsl(var(--ink-2))",
      }}>
        FINDING COLLECTORS…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{
        padding: "16px 12px", textAlign: "center",
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 13, lineHeight: 1.4, color: "hsl(var(--ink-2))",
      }}>
        No collectors to suggest yet — Fig is still early. You'll be one of the first.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
      {rows.map((r) => {
        const following = isFollowing(r.id);
        return (
          <div
            key={r.id}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px",
              border: "1px solid hsl(var(--ink-2))",
              background: "hsl(var(--paper-2, var(--cream)))",
            }}
          >
            <Swatch
              kind={(r.avatar as SwatchKind) || "olive"}
              style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 13,
                color: "hsl(var(--ink))",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {r.display_name || `@${r.handle}`}
              </div>
              <div className="caps" style={{
                color: "hsl(var(--ink-2))", fontSize: 9, marginTop: 1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                @{r.handle}
                {r.overlap > 0 && (
                  <> · {r.overlap} shared interest{r.overlap === 1 ? "" : "s"}</>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle(r.id)}
              style={{
                flexShrink: 0,
                padding: "4px 10px",
                background: following ? "transparent" : "hsl(var(--ink))",
                color: following ? "hsl(var(--ink))" : "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              {following ? "Following" : "Follow"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Profile step illustration ──────────────────────────────────────────────

interface ProfileStepProps {
  handle: string;
  displayName: string;
  avatar: SwatchKind;
  errors: { handle?: string; displayName?: string };
  theme: ThemeId;
  onTheme: (id: ThemeId) => void;
  onHandle: (v: string) => void;
  onDisplayName: (v: string) => void;
  onAvatar: (v: SwatchKind) => void;
}

function ProfileStep({ handle, displayName, avatar, errors, theme, onTheme, onHandle, onDisplayName, onAvatar }: ProfileStepProps) {
  const inputBase: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid hsl(var(--ink))",
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 13,
    color: "hsl(var(--ink))",
    outline: "none",
    boxSizing: "border-box",
  };
  const errStyle: React.CSSProperties = {
    marginTop: 4,
    fontSize: 11,
    color: "hsl(var(--rust))",
    fontFamily: "'IBM Plex Mono', monospace",
    letterSpacing: "0.05em",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Avatar swatch menu */}
      <div>
        <span className="caps" style={{ display: "block", marginBottom: 8 }}>Pick your swatch</span>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {AVATAR_OPTIONS.map((k) => {
            const selected = k === avatar;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onAvatar(k)}
                aria-label={`Avatar ${k}`}
                aria-pressed={selected}
                style={{
                  padding: 0,
                  background: "none",
                  border: selected ? "2px solid hsl(var(--ink))" : "1px solid hsl(var(--ink-2))",
                  cursor: "pointer",
                  borderRadius: 0,
                  outline: selected ? "2px solid hsl(var(--rust))" : "none",
                  outlineOffset: 2,
                }}
              >
                <Swatch kind={k} style={{ width: 48, height: 48 }} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme picker — applies live so they see the field kit shift */}
      <div>
        <span className="caps" style={{ display: "block", marginBottom: 8 }}>Pick a paper</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {THEME_IDS.map((id) => {
            const meta = THEMES[id];
            const selected = id === theme;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onTheme(id)}
                aria-pressed={selected}
                style={{
                  textAlign: "left",
                  padding: 10,
                  background: "hsl(var(--paper))",
                  border: selected ? "2px solid hsl(var(--ink))" : "1px solid hsl(var(--ink-2))",
                  outline: selected ? "2px solid hsl(var(--rust))" : "none",
                  outlineOffset: 2,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", gap: 4 }}>
                  {meta.preview.map((hsl, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: 22,
                        background: `hsl(${hsl})`,
                        border: "1px solid hsl(var(--ink))",
                      }}
                    />
                  ))}
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "hsl(var(--ink))",
                  lineHeight: 1.1,
                }}>
                  {meta.name}
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9.5,
                  letterSpacing: "0.08em",
                  color: "hsl(var(--ink-2))",
                  lineHeight: 1.3,
                }}>
                  {meta.tagline}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Handle */}
      <div>
        <span className="caps" style={{ display: "block", marginBottom: 6 }}>Handle</span>
        <div style={{ position: "relative" }}>
          <span style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: "hsl(var(--ink-2))",
            pointerEvents: "none",
          }}>@</span>
          <input
            type="text"
            value={handle}
            maxLength={20}
            onChange={(e) => onHandle(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            style={{ ...inputBase, paddingLeft: 28 }}
            placeholder="collector_handle"
          />
        </div>
        {errors.handle && <div style={errStyle}>{errors.handle}</div>}
      </div>

      {/* Display name */}
      <div>
        <span className="caps" style={{ display: "block", marginBottom: 6 }}>Display name</span>
        <input
          type="text"
          value={displayName}
          maxLength={40}
          onChange={(e) => onDisplayName(e.target.value)}
          style={inputBase}
          placeholder="What other collectors see"
        />
        {errors.displayName && <div style={errStyle}>{errors.displayName}</div>}
      </div>
    </div>
  );
}

// ─── Community step illustration ────────────────────────────────────────────
// A static legend explaining the four key social surfaces. We deliberately
// don't make these tappable — the goal is comprehension, not navigation.

function CommunityStep() {
  const items: { kicker: string; title: string; body: string; tone: string }[] = [
    {
      kicker: "FEED",
      title: "Following · Nearby · Global",
      body: "Three tabs on /feed: scans from collectors you follow, finds in your city, and the whole community.",
      tone: "hsl(var(--rust))",
    },
    {
      kicker: "FOLLOW",
      title: "Tap a handle, hit follow",
      body: "Their next scan shows up in your Following tab. Tap your own handle anytime to see your archive.",
      tone: "hsl(var(--olive))",
    },
    {
      kicker: "BRANDS",
      title: "Tap a brand to see every find",
      body: "Brand names on Home and any scan are tappable — opens a page of every posted find of that label.",
      tone: "hsl(var(--plum))",
    },
    {
      kicker: "GRAILS",
      title: "Save what you're hunting",
      body: "Add items to your Grails list and Fig pings you when another collector posts a match — even before you scan.",
      tone: "hsl(var(--ink))",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((it) => (
        <div
          key={it.kicker}
          style={{
            display: "flex",
            gap: 12,
            padding: "12px 14px",
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
          }}
        >
          <div
            style={{
              flex: "0 0 56px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              color: it.tone,
              fontWeight: 600,
              paddingTop: 2,
            }}
          >
            {it.kicker}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 15,
                fontWeight: 700,
                color: "hsl(var(--ink))",
                lineHeight: 1.2,
              }}
            >
              {it.title}
            </div>
            <div
              style={{
                marginTop: 3,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12.5,
                color: "hsl(var(--ink-2))",
                lineHeight: 1.4,
              }}
            >
              {it.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Interests step illustration ────────────────────────────────────────────
// Multi-select chip grid grouped by category. Capped at MAX_INTERESTS so the
// signal stays sharp; below MIN_INTERESTS the primary CTA stays disabled.

interface InterestsStepProps {
  selected: string[];
  onToggle: (value: string) => void;
}

function InterestsStep({ selected, onToggle }: InterestsStepProps) {
  const groups = ["Categories", "Eras", "Aesthetics", "Regions"] as const;
  const remaining = MAX_INTERESTS - selected.length;
  const meetsMin = selected.length >= MIN_INTERESTS;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          padding: "8px 12px",
          background: meetsMin ? "hsl(var(--cream))" : "hsl(var(--paper-2))",
          border: `1px ${meetsMin ? "solid" : "dashed"} hsl(var(--ink))`,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.12em",
          color: "hsl(var(--ink))",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{selected.length} / {MAX_INTERESTS} PICKED</span>
        <span style={{ color: meetsMin ? "hsl(var(--olive))" : "hsl(var(--rust))" }}>
          {meetsMin
            ? remaining > 0 ? `${remaining} MORE OK` : "FULL"
            : `PICK ${MIN_INTERESTS - selected.length} MORE`}
        </span>
      </div>

      {groups.map((group) => (
        <div key={group}>
          <div
            className="caps"
            style={{ marginBottom: 6, color: "hsl(var(--ink-2))" }}
          >
            {group}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {INTERESTS.filter((i) => i.group === group).map((interest) => {
              const isSelected = selected.includes(interest.value);
              const atCap = !isSelected && selected.length >= MAX_INTERESTS;
              return (
                <button
                  key={interest.value}
                  type="button"
                  onClick={() => !atCap && onToggle(interest.value)}
                  disabled={atCap}
                  aria-pressed={isSelected}
                  style={{
                    padding: "8px 12px",
                    background: isSelected ? "hsl(var(--ink))" : "hsl(var(--cream))",
                    color: isSelected ? "hsl(var(--paper))" : "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: atCap ? "not-allowed" : "pointer",
                    opacity: atCap ? 0.4 : 1,
                    transition: "background 120ms",
                  }}
                >
                  {isSelected && "✓ "}{interest.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
